import React, { memo, useContext, useState } from 'react';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { Col, message, Popconfirm, Row } from 'antd';
import { Table } from '@components/table';
import { ItemResponse } from '@models/item';
import { fetchItems, itemSelector } from '@redux/slices/item';
import { ModalContext } from 'src/contexts/modal-context';
import { columns } from '../constants';
import { EditModal } from './edit-modal';
import { Button } from '@styles/components/button';
import axios from '@axios';
import { AxiosResponse } from 'axios';
import { translate } from '@components/i18n';
import { SorterResult, FilterValue } from 'antd/lib/table/interface';

export const Body: React.FC = memo(() => {
    const dispatch = useDispatch();
    const itemData = useSelector(itemSelector);
    const modalContext = useContext(ModalContext);
    const [selectedRows, setSelectedRows] = useState<ItemResponse[]>([]);

    const edit = (
        data: ItemResponse,
        _: number,
        event: React.MouseEvent<HTMLElement, MouseEvent>,
    ): void => {
        if (
            !`${event.target}`.includes('SVG') &&
            !`${event.target}`.includes('Button') &&
            !`${event.target}`.includes('Span')
        )
            modalContext.setIsOpen(
                true,
                <EditModal data={data} key={`editing-item-${data.numero}`} />,
                {
                    title: `${translate('general.editItem')} - ${data.numero}`,
                    width: '40%',
                },
            );
    };

    const loadData = (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        pagination: any,
        _: Record<string, FilterValue>,
        sorter: SorterResult<unknown>,
    ): void => {
        dispatch(
            fetchItems({
                ...pagination,
                field: sorter.field,
                order: sorter.order,
                query: itemData.search.query,
                status: itemData.search.status,
            }),
        );
    };

    const rowSelection = {
        onChange: (_: React.Key[], rows: ItemResponse[]) => {
            setSelectedRows(rows);
        },
    };

    const activateItems = (ids: number[]): Promise<AxiosResponse<void>> => {
        return axios.put('/item/update-ativo', ids);
    };

    const deactivateItems = (ids: number[]): Promise<AxiosResponse<void>> => {
        return axios.put('/item/update-inativo', ids);
    };

    const { mutate: activateMutation } = useMutation(activateItems, {
        onSuccess: () => {
            setSelectedRows([]);
            dispatch(fetchItems({
                current: itemData.request.current,
                pageSize: itemData.request.pageSize,
                query: itemData.search.query,
                status: itemData.search.status,
            }));
            message.success(translate('general.itemsActivated'));
        },
        onError: () => {
            message.error(translate('general.errorActivatingItems'));
        },
    });

    const { mutate: deactivateMutation } = useMutation(deactivateItems, {
        onSuccess: () => {
            setSelectedRows([]);
            dispatch(fetchItems({
                current: itemData.request.current,
                pageSize: itemData.request.pageSize,
                query: itemData.search.query,
                status: itemData.search.status,
            }));
            message.success(translate('general.itemsDeactivated'));
        },
        onError: () => {
            message.error(translate('general.errorDeactivatingItems'));
        },
    });

    const handleActivate = (): void => {
        activateMutation(selectedRows.map((item) => item.id));
    };

    const handleDeactivate = (): void => {
        deactivateMutation(selectedRows.map((item) => item.id));
    };

    return (
        <Col>
            <Table
                columns={columns}
                rowKey={(record: ItemResponse) => `${record.id}`}
                dataSource={itemData.list}
                pagination={itemData.request}
                pointer
                rowSelection={{
                    ...rowSelection,
                    columnWidth: 48,
                    selectedRowKeys: selectedRows.map((item) => `${item.id}`),
                }}
                onRowClick={edit}
                scroll={{ y: 'calc(100vh - 380px)' }}
                onChange={loadData}
                isLoading={itemData.isFetching}
            />
            {selectedRows.length > 0 && (
                <Row justify="center" style={{ paddingBottom: 20 }}>
                    <Col span={23}>
                        <Row justify={'end'} gutter={10}>
                            <Col>
                                <Popconfirm
                                    title={
                                        <span>
                                            {translate('general.activateItemsSelected')}
                                        </span>
                                    }
                                    okText={translate('general.yes')}
                                    cancelText={translate('general.no')}
                                    onConfirm={handleActivate}
                                >
                                    <Button type="primary" height={40}>
                                        {translate('forms.buttons.activateItems')}
                                    </Button>
                                </Popconfirm>
                            </Col>
                            <Col>
                                <Popconfirm
                                    title={
                                        <span>
                                            {translate('general.deactivateItemsSelected')}
                                        </span>
                                    }
                                    okText={translate('general.yes')}
                                    cancelText={translate('general.no')}
                                    onConfirm={handleDeactivate}
                                >
                                    <Button danger type="primary" height={40}>
                                        {translate('forms.buttons.deactivateItems')}
                                    </Button>
                                </Popconfirm>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            )}
        </Col>
    );
});
